import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { getProperties, createProperty } from '@/lib/api';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Search, Plus, Building2, ChevronRight, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { UpgradeDialog } from '@/components/UpgradeDialog';

export default function PropertiesPage() {
  const [properties, setProperties] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [upgrade, setUpgrade] = useState({ open: false, message: '' });
  const [newProp, setNewProp] = useState({ name: '', address: '', city: '', state: '', zip_code: '', property_type: 'residential', units: 1 });
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEnterprise = user?.plan_tier === 'enterprise';

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await getProperties({ search: search || undefined });
      setProperties(data.items || []);
      setTotal(data.total || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [search]);

  const handleCreate = async () => {
    try {
      await createProperty(newProp);
      toast.success('Property created');
      setShowCreate(false);
      setNewProp({ name: '', address: '', city: '', state: '', zip_code: '', property_type: 'residential', units: 1 });
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Tier limit reached → show the upgrade popup with the backend message
      if (detail?.code === 'PROPERTY_LIMIT_REACHED') {
        setShowCreate(false);
        setUpgrade({ open: true, message: detail.message });
      } else {
        toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Failed to create property');
      }
    }
  };

  const handleToggleActive = async (propertyId, checked) => {
    try {
      await api.put(`/properties/${propertyId}/active`, { active: checked });
      toast.success(checked ? 'Property activated' : 'Property deactivated');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update property');
    }
  };

  const handleDeleteProperty = async (propertyId, propertyName) => {
    if (!window.confirm(`Delete "${propertyName}"? This will remove all vendor assignments. This action cannot be undone.`)) return;
    try {
      await api.delete(`/properties/${propertyId}`);
      toast.success('Property deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete property');
    }
  };

  return (
    <div data-testid="properties-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Properties</h1>
          <p className="text-sm text-muted-foreground">{total} properties</p>
        </div>
        <Button data-testid="properties-create-button" onClick={() => setShowCreate(true)}>
          <Plus className="mr-2 h-4 w-4" /> Add Property
        </Button>
      </div>

      <Card className="rounded-xl border shadow-[var(--shadow-soft)]">
        <CardContent className="p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input data-testid="properties-search-input" placeholder="Search properties..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-xl border shadow-[var(--shadow-soft)]">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-12 rounded" />)}</div>
          ) : properties.length === 0 ? (
            <div className="py-16 text-center">
              <Building2 className="mx-auto h-12 w-12 text-muted-foreground/40 mb-4" />
              <p className="text-muted-foreground">No properties found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Units</TableHead>
                  <TableHead>Vendors</TableHead>
                  {isEnterprise && <TableHead className="text-center">Vendor-Paid</TableHead>}
                  <TableHead className="text-center">Active</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {properties.map(p => (
                  <TableRow key={p.id} className="hover:bg-muted/50 transition-colors duration-150">
                    <TableCell className="cursor-pointer" onClick={() => navigate(`/properties/${p.id}`)}>
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted shrink-0">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <span className="font-medium text-sm">{p.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm cursor-pointer" onClick={() => navigate(`/properties/${p.id}`)}>{p.address}, {p.city} {p.state}</TableCell>
                    <TableCell className="cursor-pointer" onClick={() => navigate(`/properties/${p.id}`)}><Badge variant="secondary" className="text-xs capitalize">{p.property_type}</Badge></TableCell>
                    <TableCell className="text-sm tabular-nums cursor-pointer" onClick={() => navigate(`/properties/${p.id}`)}>{p.units}</TableCell>
                    <TableCell className="text-sm tabular-nums cursor-pointer" onClick={() => navigate(`/properties/${p.id}`)}>{p.vendors_count || 0}</TableCell>
                    {isEnterprise && (
                      <TableCell className="text-center" onClick={e => e.stopPropagation()}>
                        <PropertyVendorPaidToggle propertyId={p.id} initialValue={p.billing_mode === 'vendor_paid'} onChanged={load} />
                      </TableCell>
                    )}
                    <TableCell className="text-center" onClick={e => e.stopPropagation()}>
                      <Switch 
                        checked={p.active !== false} 
                        onCheckedChange={(checked) => handleToggleActive(p.id, checked)} 
                        data-testid={`property-active-toggle-${p.id}`}
                      />
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDeleteProperty(p.id, p.name)} 
                        data-testid={`property-delete-${p.id}`}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader><DialogTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Add Property</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name *</Label><Input data-testid="property-create-name-input" value={newProp.name} onChange={e => setNewProp(p => ({...p, name: e.target.value}))} /></div>
            <div><Label>Address *</Label><Input value={newProp.address} onChange={e => setNewProp(p => ({...p, address: e.target.value}))} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>City</Label><Input value={newProp.city} onChange={e => setNewProp(p => ({...p, city: e.target.value}))} /></div>
              <div><Label>State</Label><Input value={newProp.state} onChange={e => setNewProp(p => ({...p, state: e.target.value}))} /></div>
              <div><Label>ZIP</Label><Input value={newProp.zip_code} onChange={e => setNewProp(p => ({...p, zip_code: e.target.value}))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={newProp.property_type} onValueChange={v => setNewProp(p => ({...p, property_type: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residential">Residential</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="mixed">Mixed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Units</Label><Input type="number" value={newProp.units} onChange={e => setNewProp(p => ({...p, units: parseInt(e.target.value) || 1}))} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button data-testid="property-create-submit-button" onClick={handleCreate} disabled={!newProp.name || !newProp.address}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UpgradeDialog
        open={upgrade.open}
        onClose={() => setUpgrade({ open: false, message: '' })}
        title="Property Limit Reached"
        message={upgrade.message}
      />
    </div>
  );
}


function PropertyVendorPaidToggle({ propertyId, initialValue, onChanged }) {
  const [enabled, setEnabled] = useState(initialValue);

  useEffect(() => { setEnabled(initialValue); }, [initialValue]);

  const handleToggle = async (checked) => {
    setEnabled(checked);
    try {
      // Set billing mode on the property
      await api.put(`/properties/${propertyId}/billing-mode`, { billing_mode: checked ? 'vendor_paid' : 'pm_paid' });
      // Set all vendors at this property to vendor-paid
      await api.post(`/properties/${propertyId}/set-vendors-paid`, { vendor_paid: checked });
      toast.success(checked ? 'All vendors at this property set to Vendor-Paid' : 'All vendors at this property set to PM-Paid');
      onChanged?.();
    } catch (err) {
      setEnabled(!checked);
      toast.error(err.response?.data?.detail?.message || 'Failed');
    }
  };
  return <Switch checked={enabled} onCheckedChange={handleToggle} />;
}
